/**
 * The way in for someone who arrives with nothing: no key link, no texted
 * link, and nothing on this browser. Do you have a key? Yes scans it (and
 * lands on the key's own page, as a scan from the camera app would). No:
 * they need a key. Log in (top right): a returning operative signs in with
 * their number (texted code) and picks up where they left off.
 */
import { useState } from "react";
import { ENTRY } from "@/features/funnel/lib/copy";
import { LoginModal } from "@/features/funnel/components/login-modal";
import type { SignedIn } from "@/features/funnel/hooks/use-phone-login";
import {
  ACTION_BTN_CLASS,
  stepVars,
} from "@/features/stepper/steptypes/step-shell";
import { KeyScanner } from "./key-scanner";
import { KNOWN_KEY, TEST_SITE } from "./entry";

type Screen = "ask" | "scan" | "noKey" | "unknown";

/** Testing shortcut; live builds must explicitly opt in temporarily. */
const DEV_SKIP = TEST_SITE || import.meta.env.VITE_DEV_SKIP_KEY === "true";
const devSkip = () => {
  try {
    localStorage.setItem("secret-missions-ref", "dev-skip");
  } catch {
    /* private mode: the gate shows again next time */
  }
  window.location.replace("/?reset");
};

const PRIMARY = `w-full ${ACTION_BTN_CLASS} rounded-[12px] px-6 py-4 text-[17px] font-bold`;
const SECONDARY =
  "mt-3 w-full cursor-pointer rounded-[12px] border border-white/15 bg-white/[.04] px-6 py-4 text-[16px] font-semibold text-[#eadfc6] transition-colors hover:bg-white/[.08]";

export function EntryGate() {
  const [screen, setScreen] = useState<Screen>("ask");
  const [signingIn, setSigningIn] = useState(false);

  // A returning operative: to HQ, through the same kind of link we text them
  // (carrying their open mission, if they have one).
  const signedIn = (who?: SignedIn) => {
    // their number was already on an account: a returning operative, whatever
    // they have (or have not yet) filed. A number new to us needs a key.
    if (!who?.claimed) {
      setSigningIn(false);
      setScreen("unknown");
      return;
    }
    try {
      localStorage.setItem(KNOWN_KEY, "1");
    } catch {
      /* private mode: they sign in again next time */
    }
    const c = who.current;
    // HQ, their menu: it continues an open mission or offers the next one
    window.location.replace(
      c
        ? // (no t when we do not know when it was accepted: the page keeps
          // its own clock rather than restarting it now)
          `/?go=Sign%20off&m=${c.missionId}${c.acceptedAt ? `&t=${c.acceptedAt}` : ""}`
        : "/?go=Sign%20off",
    );
  };

  const copy =
    screen === "ask"
      ? { title: ENTRY.title, body: ENTRY.body }
      : screen === "unknown"
        ? { title: ENTRY.unknownTitle, body: ENTRY.unknownBody }
        : { title: ENTRY.noKeyTitle, body: ENTRY.noKeyBody };

  return (
    <section
      // the steps' own colours (the buttons' edge and text read them)
      style={stepVars({ accent: "#d6a94e", bg: "#000" })}
      className="relative flex h-[100dvh] flex-col items-center justify-center bg-black px-6 font-sans"
    >
      {screen === "scan" ? (
        <KeyScanner
          onKey={(code) =>
            window.location.assign(`/code/${encodeURIComponent(code)}`)
          }
          onBack={() => setScreen("ask")}
        />
      ) : (
        <div
          key={screen}
          className="flex w-full max-w-[380px] animate-[smGateIn_.45s_cubic-bezier(.2,.8,.3,1)_both] flex-col items-center text-center"
        >
          <div className="font-mono text-[11px] tracking-[.3em] text-[#a99d86] uppercase">
            Secret Missions
          </div>
          <h1 className="mt-4 text-[clamp(26px,8cqw,34px)] leading-[1.15] font-bold tracking-[-.01em] text-balance text-[#f4eddb]">
            {copy.title}
          </h1>
          <p className="mt-3 text-[16px] leading-[1.5] text-pretty text-[#a99d86]">
            {copy.body}
          </p>
          <div className="mt-8 w-full">
            {screen === "ask" ? (
              <>
                <button
                  type="button"
                  onClick={() => setScreen("scan")}
                  className={PRIMARY}
                >
                  {ENTRY.yes}
                </button>
                <button
                  type="button"
                  onClick={() => setScreen("noKey")}
                  className={SECONDARY}
                >
                  {ENTRY.no}
                </button>
              </>
            ) : screen === "unknown" ? (
              <>
                <button
                  type="button"
                  onClick={() => setScreen("scan")}
                  className={PRIMARY}
                >
                  {ENTRY.haveKey}
                </button>
                <button
                  type="button"
                  onClick={() => setSigningIn(true)}
                  className={SECONDARY}
                >
                  {ENTRY.otherNumber}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setScreen("scan")}
                  className={PRIMARY}
                >
                  {ENTRY.haveKeyNow}
                </button>
                <button
                  type="button"
                  onClick={() => setScreen("ask")}
                  className={SECONDARY}
                >
                  {ENTRY.back}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {screen !== "scan" ? (
        <button
          type="button"
          onClick={() => setSigningIn(true)}
          className="absolute top-[max(16px,env(safe-area-inset-top))] right-4 cursor-pointer rounded-full border border-white/15 bg-white/[.04] px-4 py-2 text-[14px] font-semibold text-[#eadfc6] transition-colors hover:bg-white/[.08]"
        >
          {ENTRY.logIn}
        </button>
      ) : null}
      {DEV_SKIP && screen !== "scan" ? (
        <button
          type="button"
          onClick={devSkip}
          className="absolute bottom-[max(16px,3dvh)] left-1/2 -translate-x-1/2 cursor-pointer rounded-md border border-white/25 bg-black/70 px-3 py-1.5 font-mono text-[10.5px] tracking-[.14em] whitespace-nowrap text-white/70 uppercase"
        >
          Dev: skip, I have a key
        </button>
      ) : null}
      <LoginModal
        open={signingIn}
        onClose={() => setSigningIn(false)}
        onSignedIn={signedIn}
        // no "You're in": a known number goes straight to HQ, an unknown
        // one is told we don't see them
        successMessage=""
        // a number new to us is not kept: signing in again with it must still
        // be "we don't see you", not a way in without a key
        signInOnly
      />
      <style>{`@keyframes smGateIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}`}</style>
    </section>
  );
}
